// /security — staff visibility into prompt-injection / canary / SSRF
// signals + rate-limit summary. Tabbed layout mirrors the Money page;
// each tab fetches its own slice and renders a table with drill-down.
//
// Mark-false-positive lives in a row dialog so the staff intent is
// explicit (a click + reason). Revoke key / revoke session live on the
// Tenants detail page (P1) — we don't duplicate them here.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleSlash, ShieldQuestion } from 'lucide-react';
import { toast } from 'sonner';
import { useApiClient } from '../../lib/providers.js';
import {
  SECURITY_CANARY_QUERY_KEY,
  SECURITY_INJECTION_QUERY_KEY,
  SECURITY_RATE_LIMITS_QUERY_KEY,
  SECURITY_SSRF_QUERY_KEY,
  extractRiskLevel,
  riskBadgeVariant,
  securityApi,
  type RateLimitSummary,
  type SecurityEvent,
} from '../../lib/api/security.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog.js';
import { Label } from '../../components/ui/label.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs.js';
import { Textarea } from '../../components/ui/textarea.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { formatDateTime } from '../../lib/format-date.js';

export function SecurityPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Security signals"
        description="Prompt-injection flags, canary leaks, SSRF blocks, and rate-limit counters across every tenant."
      />

      <Tabs defaultValue="injection">
        <TabsList>
          <TabsTrigger value="injection">Injection</TabsTrigger>
          <TabsTrigger value="canary">Canary</TabsTrigger>
          <TabsTrigger value="ssrf">SSRF</TabsTrigger>
          <TabsTrigger value="rate-limits">Rate-limits</TabsTrigger>
        </TabsList>

        <TabsContent value="injection" className="mt-6">
          <InjectionTab />
        </TabsContent>
        <TabsContent value="canary" className="mt-6">
          <CanaryTab />
        </TabsContent>
        <TabsContent value="ssrf" className="mt-6">
          <SsrfTab />
        </TabsContent>
        <TabsContent value="rate-limits" className="mt-6">
          <RateLimitsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Injection tab ────────────────────────────────────────────────

function InjectionTab() {
  const client = useApiClient();
  const query = useQuery({
    queryKey: SECURITY_INJECTION_QUERY_KEY,
    queryFn: () => securityApi.injection(client, { sinceHours: 168, limit: 200 }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Prompt-injection flags (last 7 days)</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        <EventsBody
          query={query}
          variant="injection"
          empty={{
            title: 'No injection flags',
            description: 'The chat route emits these whenever the heuristic detector matches.',
          }}
        />
      </CardContent>
    </Card>
  );
}

// ─── Canary tab ───────────────────────────────────────────────────

function CanaryTab() {
  const client = useApiClient();
  const query = useQuery({
    queryKey: SECURITY_CANARY_QUERY_KEY,
    queryFn: () => securityApi.canary(client, { sinceHours: 168, limit: 200 }),
  });
  return (
    <>
      <DeferredBanner
        text="Output-side canary + system-prompt-leak emitters are in the security package today; persistence to audit_log is a follow-up — see admin-dashboard-plan-v2 §12."
      />
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">Canary / leak events (last 7 days)</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <EventsBody
            query={query}
            variant="default"
            empty={{
              title: 'No canary events',
              description: 'Either the output filter is healthy or no emitter is wired yet.',
            }}
          />
        </CardContent>
      </Card>
    </>
  );
}

// ─── SSRF tab ─────────────────────────────────────────────────────

function SsrfTab() {
  const client = useApiClient();
  const query = useQuery({
    queryKey: SECURITY_SSRF_QUERY_KEY,
    queryFn: () => securityApi.ssrf(client, { sinceHours: 168, limit: 200 }),
  });
  return (
    <>
      <DeferredBanner text="The SSRF guard throws SsrfBlockedError today; persistence to audit_log is a follow-up — tracked in admin-dashboard-plan-v2 §12 item 4." />
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">SSRF block events (last 7 days)</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <EventsBody
            query={query}
            variant="default"
            empty={{
              title: 'No SSRF blocks',
              description: 'Either no outbound calls were rejected or the audit emit is not yet wired.',
            }}
          />
        </CardContent>
      </Card>
    </>
  );
}

// ─── Rate-limits tab ──────────────────────────────────────────────

function RateLimitsTab() {
  const client = useApiClient();
  const query = useQuery({
    queryKey: SECURITY_RATE_LIMITS_QUERY_KEY,
    queryFn: () => securityApi.rateLimits(client),
  });

  if (query.isPending) return <Skeleton className="h-32 w-full" />;
  if (query.isError) {
    return (
      <ErrorRetry
        title="Failed to load rate-limit summary"
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const summary: RateLimitSummary = query.data;
  return (
    <>
      <DeferredBanner text={summary.note} />
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {summary.layers.map((l) => (
          <Card key={l.layer} className={l.available ? '' : 'opacity-60'}>
            <CardContent className="space-y-1 py-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                {l.layer}
              </div>
              <div className="text-2xl font-semibold tabular-nums">
                {l.available ? l.hits.toLocaleString() : '—'}
              </div>
              <div className="text-xs text-muted-foreground">
                {l.available ? 'hits in window' : 'counter not wired'}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

// ─── Shared body for injection / canary / ssrf tables ─────────────

function EventsBody({
  query,
  variant,
  empty,
}: {
  query: ReturnType<typeof useQuery<{ rows: SecurityEvent[] }>>;
  variant: 'injection' | 'default';
  empty: { title: string; description: string };
}) {
  if (query.isPending) {
    return (
      <div className="space-y-2 px-4 py-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="px-4">
        <ErrorRetry
          title="Failed to load events"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </div>
    );
  }
  if (query.data.rows.length === 0) {
    return (
      <div className="px-4">
        <EmptyState title={empty.title} description={empty.description} />
      </div>
    );
  }

  return <EventsTable rows={query.data.rows} variant={variant} />;
}

function EventsTable({
  rows,
  variant,
}: {
  rows: readonly SecurityEvent[];
  variant: 'injection' | 'default';
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Time</TableHead>
          <TableHead>Tenant</TableHead>
          <TableHead>Action</TableHead>
          {variant === 'injection' && <TableHead>Risk</TableHead>}
          <TableHead>IP</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
              {formatDateTime(row.ts)}
            </TableCell>
            <TableCell>
              <Link to={`/tenants/${row.tenantId}`} className="hover:text-foreground">
                {row.tenantName}
              </Link>
            </TableCell>
            <TableCell className="text-xs font-mono">{row.action}</TableCell>
            {variant === 'injection' && (
              <TableCell>
                <Badge variant={riskBadgeVariant(extractRiskLevel(row.meta))}>
                  {extractRiskLevel(row.meta)}
                </Badge>
              </TableCell>
            )}
            <TableCell className="text-xs text-muted-foreground">
              {row.ip ?? '—'}
            </TableCell>
            <TableCell className="text-right">
              <div className="inline-flex gap-2">
                <DetailDialog event={row} />
                <MarkFpDialog event={row} />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DetailDialog({ event }: { event: SecurityEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        <ShieldQuestion aria-hidden className="mr-1 h-3.5 w-3.5" />
        View
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{event.action}</DialogTitle>
          <DialogDescription>
            {formatDateTime(event.ts)} · {event.tenantName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <KV k="Tenant id" v={event.tenantId} />
          <KV k="Target" v={event.target ?? '—'} />
          <KV k="IP" v={event.ip ?? '—'} />
          <KV k="User agent" v={event.userAgent ?? '—'} mono />
          <div>
            <Label className="text-xs text-muted-foreground">Meta</Label>
            <pre className="mt-1 max-h-72 overflow-auto rounded-md border bg-muted/30 p-3 text-xs">
              {JSON.stringify(event.meta, null, 2)}
            </pre>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MarkFpDialog({ event }: { event: SecurityEvent }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () => securityApi.markFalsePositive(client, event.id, reason.trim() || undefined),
    onSuccess: () => {
      toast.success('Marked as false positive');
      void qc.invalidateQueries({ queryKey: ['admin', 'security'] });
      setOpen(false);
      setReason('');
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Mark failed');
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        <CircleSlash aria-hidden className="mr-1 h-3.5 w-3.5" />
        Mark FP
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark as false positive</DialogTitle>
          <DialogDescription>
            Appends a <code>staff_audit_log</code> row pointing at this event id.
            The original audit entry stays immutable; the soft-mark gives staff
            context when the same heuristic fires again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="fp-reason">Reason (optional)</Label>
          <Textarea
            id="fp-reason"
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder="e.g. verified — automated probe from Lighthouse run"
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={mut.isPending}
            onClick={() => {
              mut.mutate();
            }}
          >
            {mut.isPending ? 'Marking…' : 'Mark FP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className={mono ? 'truncate text-right font-mono text-xs' : 'truncate text-right'}>
        {v}
      </span>
    </div>
  );
}

function DeferredBanner({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300">
      <strong>Partial signal:</strong> {text}
    </div>
  );
}
